import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import '../GetLabelsPage.css';
import ReplacementLabel from './ReplacementLabel';

// ── PDF helpers ───────────────────────────────────────────────────────────────

function formatBoxNumber(n) {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const idx = Math.floor((n - 1) / 9999);
  const num = ((n - 1) % 9999) + 1;
  return `${letters[idx] || 'Z'}${String(num).padStart(4, '0')}`;
}

async function qrToDataUrl(text) {
  const canvas = document.createElement('canvas');
  await QRCode.toCanvas(canvas, text, { margin: 1, width: 220, errorCorrectionLevel: 'M' });
  return canvas.toDataURL('image/png');
}

const LH = (pt) => pt * 0.3528 * 1.08;

async function generateLabelPDF(productName, sku, unitsPerBox, labelCount, startNumber) {
  const W = 50;
  const H = 25;

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [W, H] });

  const qrMargin = 0.75;
  const qrSize   = H - 2 * qrMargin;
  const qrX      = W - qrSize - qrMargin;
  const qrY      = qrMargin;

  const tPadX  = 1.5;
  const tPadY  = 1.5;
  const colLeft = tPadX;
  const colW    = qrX - tPadX - 1;
  const colCX   = colLeft + colW / 2;
  const textW   = colW;
  const availH  = H - 2 * tPadY;

  const boxPt  = 10;
  const boxLH  = LH(boxPt);
  const gapNB  = 1.0;
  const gapBS  = 0.7;

  for (let i = 0; i < labelCount; i++) {
    if (i > 0) pdf.addPage([W, H], 'landscape');

    const boxCode   = formatBoxNumber(startNumber + i);
    const qrContent = `${productName}, ${sku}, BOX#${boxCode}/${unitsPerBox}`;

    const qrDataUrl = await qrToDataUrl(qrContent);
    pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

    let skuPt = 8;
    while (skuPt >= 4) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(skuPt);
      if (pdf.getTextWidth(sku) <= textW) break;
      skuPt -= 0.5;
    }
    const skuLH = LH(skuPt);

    const fixedH    = gapNB + boxLH + boxLH + gapBS + skuLH;
    const availName = availH - fixedH;

    let namePt = 18;
    let nameLines;
    while (namePt >= 4) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(namePt);
      nameLines = pdf.splitTextToSize(productName, textW);
      if (nameLines.length * LH(namePt) <= availName) break;
      namePt -= 0.5;
    }

    const nameTH = nameLines.length * LH(namePt);
    const blockH = nameTH + fixedH;

    let y = tPadY + Math.max(0, (availH - blockH) / 2);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(namePt);
    nameLines.forEach((line) => {
      pdf.text(line, colCX, y, { align: 'center', baseline: 'top' });
      y += LH(namePt);
    });

    y += gapNB;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(boxPt);
    pdf.text('Box#', colCX, y, { align: 'center', baseline: 'top' });
    y += boxLH;

    pdf.text(`${boxCode}/${unitsPerBox}`, colCX, y, { align: 'center', baseline: 'top' });
    y += boxLH + gapBS;

    pdf.setFontSize(skuPt);
    pdf.text(sku, colCX, y, { align: 'center', baseline: 'top' });
  }

  return pdf;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ReadyProductBoxes = () => {
  const [brands,         setBrands]         = useState([]);
  const [selectedBrand,  setSelectedBrand]  = useState('');
  const [products,       setProducts]       = useState([]);
  const [productSearch,  setProductSearch]  = useState('');
  const [showDropdown,   setShowDropdown]   = useState(false);
  const [selectedItems,  setSelectedItems]  = useState([]);
  const [generating,     setGenerating]     = useState(false);
  const [progress,       setProgress]       = useState(0);
  const [progressLabel,  setProgressLabel]  = useState('');
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [error,          setError]          = useState('');

  const searchRef   = useRef(null);
  const searchInput = useRef(null);

  useEffect(() => {
    api.get('/api/labels/brands')
      .then(r => setBrands(r.data.data))
      .catch(() => setError('Failed to load brands'));
  }, []);

  useEffect(() => {
    const url = selectedBrand
      ? `/api/labels/ready-products?brandID=${selectedBrand}`
      : `/api/labels/ready-products`;
    api.get(url)
      .then(r => setProducts(r.data.data || []))
      .catch(() => setError('Failed to load ready products'));
  }, [selectedBrand]);

  useEffect(() => {
    const h = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filteredProducts = products.filter(
    (p) =>
      !selectedItems.find((i) => i.sku === p.SKU) &&
      ((p.name || '').toLowerCase().includes(productSearch.toLowerCase()) ||
        p.SKU.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const addProduct = (product) => {
    setSelectedItems((prev) => [
      ...prev,
      { sku: product.SKU, name: product.name || product.SKU, unitsPerBox: product.unitsPerBox || 1, qty: 1 },
    ]);
    setProductSearch('');
    setShowDropdown(true);
    searchInput.current?.focus();
  };

  const updateItem = (sku, field, value) =>
    setSelectedItems((prev) => prev.map((i) => (i.sku === sku ? { ...i, [field]: value } : i)));

  const removeItem = (sku) =>
    setSelectedItems((prev) => prev.filter((i) => i.sku !== sku));

  const handleGenerate = async () => {
    if (selectedItems.length === 0) return;
    setGenerating(true);
    setProgress(0);
    setProgressLabel('');
    setGeneratedFiles([]);
    setError('');

    try {
      const reserveRes = await api.post(
        '/api/labels/reserve-boxes',
        { items: selectedItems.map((i) => ({ SKU: i.sku, count: i.qty })) }
      );
      const reservations = reserveRes.data.data;

      const files = [];
      for (let idx = 0; idx < selectedItems.length; idx++) {
        const item        = selectedItems[idx];
        const reservation = reservations.find((r) => r.SKU === item.sku);
        setProgressLabel(`Generating: ${item.name}`);

        const pdf  = await generateLabelPDF(item.name, item.sku, item.unitsPerBox, item.qty, reservation.startNumber);
        const blob = pdf.output('blob');
        files.push({ name: item.name, sku: item.sku, blob });
        setProgress(Math.round(((idx + 1) / selectedItems.length) * 100));
      }

      setGeneratedFiles(files);
    } catch (err) {
      setError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
      setProgressLabel('');
    }
  };

  const downloadFile = (file) => {
    const url = URL.createObjectURL(file.blob);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = `${file.name}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAll = () => generatedFiles.forEach(downloadFile);
  const reset       = () => { setGeneratedFiles([]); setProgress(0); };

  return (
    <div className="gl-content">

      {error && <div className="gl-error">{error}</div>}

      {/* Brand filter */}
      <div className="gl-brand-wrap">
        <label className="gl-label">Brand (optional)</label>
        <select
          className="gl-select"
          value={selectedBrand}
          onChange={(e) => {
            setSelectedBrand(e.target.value);
            setGeneratedFiles([]);
            setProductSearch('');
          }}
        >
          <option value="">— All brands —</option>
          {brands.map((b) => (
            <option key={b.brandID} value={b.brandID}>{b.brandName}</option>
          ))}
        </select>
      </div>

      {/* Product search */}
      <div className="gl-search-wrap" ref={searchRef}>
        <label className="gl-label">Add Products</label>
        <input
          ref={searchInput}
          type="text"
          className="gl-input"
          value={productSearch}
          onChange={(e) => { setProductSearch(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search by name or SKU — click to add, search again to add more…"
        />

        {showDropdown && (productSearch || filteredProducts.length > 0) && (
          <div className="gl-dropdown">
            {filteredProducts.length === 0 ? (
              <div className="gl-dropdown-empty">
                {productSearch ? 'No products found' : 'Start typing to search…'}
              </div>
            ) : (
              filteredProducts.map((p) => (
                <div
                  key={p.SKU}
                  className="gl-dropdown-item"
                  onMouseDown={() => addProduct(p)}
                >
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>{p.SKU}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Selected items table */}
      {selectedItems.length > 0 && (
        <div className="gl-table-wrap">
          <table className="gl-table">
            <thead>
              <tr>
                <th className="gl-th">Product</th>
                <th className="gl-th gl-th-w110">SKU</th>
                <th className="gl-th gl-th-w120 gl-th-center">Units / Box</th>
                <th className="gl-th gl-th-w120 gl-th-center">Label Count</th>
                <th className="gl-th gl-th-w40 gl-th-center" />
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item) => (
                <tr key={item.sku}>
                  <td className="gl-td">{item.name}</td>
                  <td className="gl-td gl-td-muted">{item.sku}</td>
                  <td className="gl-td gl-td-center">
                    <input
                      type="number" min="1"
                      className="gl-num-input"
                      value={item.unitsPerBox}
                      onChange={(e) => updateItem(item.sku, 'unitsPerBox', parseInt(e.target.value) || 1)}
                    />
                  </td>
                  <td className="gl-td gl-td-center">
                    <input
                      type="number" min="1"
                      className="gl-num-input"
                      value={item.qty}
                      onChange={(e) => updateItem(item.sku, 'qty', parseInt(e.target.value) || 1)}
                    />
                  </td>
                  <td className="gl-td gl-td-center">
                    <button className="gl-remove-btn" onClick={() => removeItem(item.sku)} title="Remove">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate button */}
      {selectedItems.length > 0 && !generating && generatedFiles.length === 0 && (
        <button
          className="gl-btn gl-btn-primary gl-btn-generate"
          onClick={handleGenerate}
        >
          Generate Labels
        </button>
      )}

      {/* Progress bar */}
      {generating && (
        <div className="gl-progress-wrap">
          <div className="gl-progress-label">
            {progressLabel || 'Generating…'} — {progress}%
          </div>
          <div className="gl-progress-track">
            <div className="gl-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Download section */}
      {generatedFiles.length > 0 && (
        <div>
          <div className="gl-done-header">
            <span className="gl-done-label">
              Done! {generatedFiles.length} file{generatedFiles.length > 1 ? 's' : ''} ready
            </span>
            {generatedFiles.length > 1 && (
              <button className="gl-btn gl-btn-primary" onClick={downloadAll}>
                Download All
              </button>
            )}
            <button className="gl-btn gl-btn-gray" onClick={reset}>
              New Batch
            </button>
          </div>

          <div className="gl-files-list">
            {generatedFiles.map((f, i) => (
              <div key={i} className="gl-file-item">
                <div>
                  <span className="gl-file-name">{f.name}</span>
                  <span className="gl-file-sku">{f.sku}</span>
                </div>
                <button
                  className="gl-btn gl-btn-primary gl-btn-sm"
                  onClick={() => downloadFile(f)}
                >
                  Download PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ReplacementLabel
        numberLabel="Box number"
        products={products}
        unitsKey="unitsPerBox"
        fileName={(code, p) => `${p.name || p.SKU} Box ${code} (replacement)`}
        generate={(n, p, units) => generateLabelPDF(p.name || p.SKU, p.SKU, units, 1, n)}
      />

    </div>
  );
};

export default ReadyProductBoxes;
